import React, { createContext, useCallback, useEffect, useState } from "react";
import {
  AuthFormValues,
  AuthResponse,
  ErrorMessage,
  ResendTanRequest,
  ValidateTanRequest,
  ValidateTanResponse,
} from "../interfaces/auth.interface";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/AuthService";
import { useNotification } from "../hooks/useNotification";
import { useTranslation } from "react-i18next";
import * as uuid from "uuid";
import {
  trackOnAuthenticationPageAlreadyPaid,
  trackOnStandardError,
} from "../adobeTracking/trackers";
import { usePathname } from "../hooks/usePathname";
import { Pathname } from "../enums";
import { convertToJson } from "../helpers";

export type AuthContextType = {
  auth: any;
  authResponse: AuthResponse | undefined;
  handleAuth: (
    auth: any,
    captchaToken: string,
    onCaptchaError?: () => void,
    onError?: () => void,
  ) => void;
  handleResendTan: () => void;
  handleSecondAuthValidation: (tan: string) => void;
  loading: boolean;
  login: ValidateTanResponse | undefined;
  pathname: string;
  setAuth: React.Dispatch<any>;
  setLoading: React.Dispatch<boolean>;
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: React.ReactNode;
}

const CONTRACT_ID_PARAM_NAME = "ref_id";

const getInitialValues = (pathname: string) => {
  switch (pathname) {
    case Pathname.FLEET:
      return {
        customerNumber: "",
        companyName: "",
        siren: "",
        contractID: undefined,
      };
    case Pathname.SME:
      return {
        surName: "",
        firstName: "",
        dateOfBirth: undefined,
        siren: "",
        contractID: undefined,
      };
    default:
      return {
        surName: "",
        firstName: "",
        dateOfBirth: undefined,
        contractID: undefined,
      };
  }
};

// The form keeps the date as DD/MM/YYYY, the API expects YYYY-MM-DD
const mapAuthRequest = (
  pathname: string,
  values: AuthFormValues,
): AuthFormValues => {
  const mapsDateOfBirth =
    pathname === Pathname.RETAIL || pathname === Pathname.SME;
  if (mapsDateOfBirth && "dateOfBirth" in values) {
    return {
      ...values,
      dateOfBirth: values.dateOfBirth?.split("/").reverse().join("-"),
    };
  }
  return values;
};

const oneLineErrorMessagesTypes = new Set([
  "NO_OUTSTANDING_BALANCE_FOUND",
  "DATE_OF_BIRTH_INVALID_FORMAT",
  "SIREN_IS_MANDATORY_FOR_SME",
]);

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { handleCreateNotification, handleDeleteAllNotifications } =
    useNotification();
  const { pathname } = usePathname();
  const { t } = useTranslation("errors");
  const [resendCodeEntries, setResendCodeEntries] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [auth, setAuth] = useState<any>(getInitialValues(pathname));
  const [authResponse, setAuthResponse] = useState<AuthResponse | undefined>();
  const [login, setLogin] = useState<ValidateTanResponse | undefined>(
    undefined,
  );

  const handleAuth = useCallback(
    async (
      auth: any,
      captchaToken: string,
      onCaptchaError?: () => void,
      onError?: () => void,
    ) => {
      try {
        setAuth(auth);
        const authService = new AuthService(pathname);
        const { status, data } = await authService.auth(
          mapAuthRequest(pathname, auth),
          captchaToken,
        );
        const errorValue =
          data?.errorMessage && typeof data.errorMessage === "string"
            ? convertToJson(data.errorMessage)
            : data.errorMessage;
        const errorMessage = data?.errorMessage
          ? Array.isArray(errorValue)
            ? errorValue[0]
            : data.errorMessage
          : null;
        if (status !== 200) {
          if (errorMessage === "Captcha validation failed.") {
            onCaptchaError?.();
            setLoading(false);
            return;
          }
          onError?.();
          handleCreateNotification({
            id: uuid.v4(),
            status: "error",
            title:
              status >= 500 ? t(`requestErrors.auth.500.title`) : undefined,
            body: (
              <React.Fragment key=".0">
                {status === 404 || status === 400 ? (
                  status === 404 ||
                  (status === 400 &&
                    !oneLineErrorMessagesTypes.has(errorMessage)) ? (
                    <>
                      <p className="u-mb">
                        {t(
                          `requestErrors.auth.${status}.${errorMessage}.text1`,
                        )}
                      </p>
                      <p>
                        {t(
                          `requestErrors.auth.${status}.${errorMessage}.text2`,
                        )}
                      </p>
                    </>
                  ) : (
                    status === 400 &&
                    oneLineErrorMessagesTypes.has(errorMessage) && (
                      <>
                        {t(
                          `requestErrors.auth.${status}.${errorMessage}.text1`,
                        )}
                      </>
                    )
                  )
                ) : (
                  <>
                    <p className="u-mb">{t(`requestErrors.auth.500.text1`)}</p>
                    <p>{t(`requestErrors.auth.500.text2`)}</p>
                  </>
                )}
              </React.Fragment>
            ),
          });
          setLoading(false);
          return;
        }
        if (status === 200 && data?.transactionStatus === "PAYMENT-FINISHED") {
          handleCreateNotification({
            id: uuid.v4(),
            status: "success",
            title: undefined,
            body: (
              <React.Fragment key=".0">
                <>
                  <p className="u-mb">{t(`paymentFinished.text1`)}</p>
                  <p>{t(`paymentFinished.text2`)}</p>
                </>
              </React.Fragment>
            ),
          });
          setLoading(false);
          trackOnAuthenticationPageAlreadyPaid();
          return;
        }

        setAuthResponse(data as AuthResponse);
        handleDeleteAllNotifications();
        navigate("second-auth-factor");
        setLoading(false);
      } catch (error) {
        onError?.();
        handleCreateNotification({
          id: uuid.v4(),
          status: "error",
          title: t(`requestErrors.auth.500.title`),
          body: (
            <React.Fragment key=".0">
              <>
                <p>{t(`requestErrors.auth.500.text1`)}</p>
                <p>{t(`requestErrors.auth.500.text2`)}</p>
              </>
            </React.Fragment>
          ),
        });
        setLoading(false);
        trackOnStandardError("Internal Server Error");
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth, t, navigate],
  );

  const handleSecondAuthValidation = useCallback(
    async (tan: string) => {
      try {
        const authService = new AuthService(pathname);
        const { status, data } = await authService.validateTan({
          transactionId: authResponse?.transactionId,
          tan: tan,
          nonce: authResponse?.nonce,
        } as ValidateTanRequest);
        if (status !== 200) {
          handleCreateNotification({
            id: uuid.v4(),
            status: status === 400 ? "warning" : "error",
            title:
              status !== 400 ||
              (status === 400 && data.errorMessage === "USER_TEMPORARY_LOCKED")
                ? status < 500
                  ? t(`requestErrors.secondAuthFact.${status}.title`)
                  : t(`requestErrors.secondAuthFact.500.title`)
                : undefined,
            body: (
              <React.Fragment key=".0">
                {status === 400 &&
                (data.errorMessage as ErrorMessage)?.message ===
                  "Provided TAN is not VALID." ? (
                  <>{t(`requestErrors.secondAuthFact.missMatch.text1`)}</>
                ) : status === 400 &&
                  data.errorMessage === "USER_TEMPORARY_LOCKED" ? (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.secondAuthFact.maxEntries.text1`)}
                    </p>
                    <p>{t(`requestErrors.secondAuthFact.maxEntries.text2`)}</p>
                  </>
                ) : status === 500 ? (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.secondAuthFact.500.text1`)}
                    </p>
                    <p>{t(`requestErrors.secondAuthFact.500.text2`)}</p>
                  </>
                ) : (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.secondAuthFact.${status}.text1`)}
                    </p>
                    <p>{t(`requestErrors.secondAuthFact.${status}.text2`)}</p>
                  </>
                )}
              </React.Fragment>
            ),
          });
          setLoading(false);
          return;
        }
        handleDeleteAllNotifications();
        navigate("overview");
        setLogin(data);
        setLoading(false);
      } catch (error) {
        handleCreateNotification({
          id: uuid.v4(),
          status: "error",
          title: t(`requestErrors.secondAuthFact.500.title`),
          body: (
            <React.Fragment key=".0">
              <>
                <p className="u-mb">
                  {t(`requestErrors.secondAuthFact.500.text1`)}
                </p>
                <p>{t(`requestErrors.secondAuthFact.500.text2`)}</p>
              </>
            </React.Fragment>
          ),
        });
        setLoading(false);
        trackOnStandardError("Internal Server Error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleCreateNotification, setLoading, t, authResponse, navigate],
  );
  const handleResendTan = useCallback(async () => {
    try {
      if (resendCodeEntries < 3) {
        setResendCodeEntries(resendCodeEntries + 1);
        const authService = new AuthService(pathname);
        const { status, data } = await authService.resendTan({
          transactionId: authResponse?.transactionId,
          nonce: authResponse?.nonce,
        } as ResendTanRequest);
        if (status !== 200) {
          var expiracyDate = new Date();
          expiracyDate.setSeconds(expiracyDate.getSeconds() + 60);
          handleCreateNotification({
            id: uuid.v4(),
            status: status === 400 ? "warning" : "error",
            title:
              status !== 400
                ? t(`requestErrors.secondAuthFact.${status}.title`)
                : status >= 500
                  ? t(`requestErrors.secondAuthFact.500.title`)
                  : undefined,
            body: (
              <React.Fragment key=".0">
                {status === 400 &&
                data.errorMessage !== "USER_TEMPORARY_LOCKED" ? (
                  <>{t(`requestErrors.secondAuthFact.${status}.text1`)}</>
                ) : status === 400 &&
                  data.errorMessage === "USER_TEMPORARY_LOCKED" ? (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.secondAuthFact.maxEntries.text1`)}
                    </p>
                    <p>{t(`requestErrors.secondAuthFact.maxEntries.text2`)}</p>
                  </>
                ) : status >= 500 ? (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.secondAuthFact.500.text1`)}
                    </p>
                    <p>{t(`requestErrors.secondAuthFact.500.text2`)}</p>
                  </>
                ) : (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.secondAuthFact.${status}.text1`)}
                    </p>
                    <p>{t(`requestErrors.secondAuthFact.${status}.text2`)}</p>
                  </>
                )}
              </React.Fragment>
            ),
          });
          setLoading(false);
          return;
        }
        setAuthResponse({ ...authResponse, tan: data.tan });
        setLoading(false);
      } else {
        setResendCodeEntries(resendCodeEntries + 1);
        handleCreateNotification({
          id: uuid.v4(),
          status: "error",
          title: undefined,
          body: (
            <React.Fragment key=".0">
              <>
                <p className="u-mb">
                  {t(`requestErrors.secondAuthFact.maxEntries.text1`)}
                </p>
                <p>{t(`requestErrors.secondAuthFact.maxEntries.text2`)}</p>
              </>
            </React.Fragment>
          ),
        });
        setLoading(false);
        return;
      }
    } catch (error) {
      handleCreateNotification({
        id: uuid.v4(),
        status: "error",
        title: t(`requestErrors.secondAuthFact.500.title`),
        body: (
          <React.Fragment key=".0">
            <>
              <p className="u-mb">
                {t(`requestErrors.secondAuthFact.500.text1`)}
              </p>
              <p>{t(`requestErrors.secondAuthFact.500.text2`)}</p>
            </>
          </React.Fragment>
        ),
      });
      setLoading(false);
      trackOnStandardError("Internal Server Error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleCreateNotification,
    setLoading,
    t,
    authResponse,
    resendCodeEntries,
    setResendCodeEntries,
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const contractId = searchParams.get(CONTRACT_ID_PARAM_NAME) ?? "0";
    if (!contractId || contractId === "0") {
      if (
        window.location.href.includes("maintenance") ||
        window.location.href.includes("forbidden") ||
        window.location.href.includes("not-found")
      ) {
        return;
      }
      navigate("errors/not-authorized");
      // navigate("confirmation/error");
      return;
    } else {
      setAuth((values: any) => ({
        ...values,
        contractID: parseInt(contractId),
      }));
    }
    if (window.location.hash.length <= 2) setLogin(undefined);
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        auth,
        authResponse,
        handleAuth,
        handleResendTan,
        handleSecondAuthValidation,
        loading,
        login,
        pathname,
        setAuth,
        setLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
